// apiService.js
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/';

/**
 * Generic POST request handler
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @returns {Promise<Object>} - API response
 */
const postRequest = async (endpoint, data = {}) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-type": "application/json" },
    body: JSON.stringify(data),
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
    const responseData = await response.json();
    
    if (responseData.hasOwnProperty('status') && responseData.status === 200) {
      return { success: true, data: responseData };
    } else {
      const errorMessage = responseData.hasOwnProperty('message') 
        ? responseData.message 
        : 'Invalid API Response';
      console.error(`API Error ${responseData.status || 'Unknown'}:`, errorMessage);
      return { success: false, error: errorMessage, status: responseData.status };
    }
  } catch (error) {
    console.error('Network/Parse Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * File upload request handler
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - FormData object containing files and other data
 * @returns {Promise<Object>} - API response
 */
const postFileRequest = async (endpoint, formData) => {
  const requestOptions = {
    method: "POST",
    // Don't set Content-Type header - let browser set it for multipart/form-data
    body: formData,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
    const responseData = await response.json();
    
    if (responseData.hasOwnProperty('status') && responseData.status === 200) {
      return { success: true, data: responseData };
    } else {
      const errorMessage = responseData.hasOwnProperty('message') 
        ? responseData.message 
        : 'Invalid API Response';
      console.error(`API Error ${responseData.status || 'Unknown'}:`, errorMessage);
      return { success: false, error: errorMessage, status: responseData.status };
    }
  } catch (error) {
    console.error('Network/Parse Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generic GET request handler
 * @param {string} endpoint - API endpoint
 * @param {Object} params - URL parameters (optional)
 * @returns {Promise<Object>} - API response
 */
const getRequest = async (endpoint, params = {}) => {
  let url = `${API_BASE_URL}${endpoint}`;
  
  // Add query params if any
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => searchParams.append(key, params[key]));
    url += `?${searchParams.toString()}`;
  }

  try {
    const response = await fetch(url);
    const responseData = await response.json();
    
    if (responseData.hasOwnProperty('status') && responseData.status === 200) {
      return { success: true, data: responseData };
    } else {
      const errorMessage = responseData.hasOwnProperty('message') 
        ? responseData.message 
        : 'Invalid API Response';
      console.error(`API Error ${responseData.status || 'Unknown'}:`, errorMessage);
      return { success: false, error: errorMessage, status: responseData.status };
    }
  } catch (error) {
    console.error('Network/Parse Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Batch POST requests to multiple endpoints
 * @param {Array} endpointConfigs - Array of endpoint configuration objects
 * @param {Object} postData - Data to send in POST requests
 * @param {boolean} condition - Boolean condition for conditional loading (e.g., whether to load on specific filter changes)
 * @param {boolean} force - Whether to force load all endpoints
 * @param {Function} setLoading - Optional callback to update loading state
 * @returns {Promise<void>}
 */
const batchPostRequests = async (endpointConfigs, postData, condition = false, force = false, setLoading = null) => {
  if (setLoading) setLoading(true);
  
  try {
    // Sort by isFast to prioritize faster endpoints
    const sortedConfigs = [...endpointConfigs].sort((a, b) => 
      (a.isFast === b.isFast) ? 0 : a.isFast ? -1 : 1
    );

    for (const config of sortedConfigs) {
      const shouldLoad = (force && config.force) || 
                        (!force && (!condition || config.loadOnCondition));

      if (shouldLoad) {
        // Clear previous data by calling callback with null
        if (config.callback) {
          config.callback(null);
        }

        const result = await postRequest(config.endpoint, postData);
        
        if (result.success && config.callback) {
          config.callback(result.data);
        }
        // Errors are already logged in postRequest
      }
    }
  } finally {
    if (setLoading) setLoading(false);
  }
};

/**
 * Batch GET requests for initialization data
 * @param {Array} endpointConfigs - Array of endpoint configuration objects
 * @param {Function} setInitialized - Optional callback to update initialized state
 * @returns {Promise<Object>} - Object with results keyed by endpoint or config key
 */
const batchGetRequests = async (endpointConfigs, setInitialized = null) => {
  const results = {};

  try {
    for (const config of endpointConfigs) {
      const result = await getRequest(config.endpoint, config.params || {});
      
      if (result.success && config.callback) {
        config.callback(result.data);
      }
      
      // Store result using key or endpoint as identifier
      const resultKey = config.key || config.endpoint;
      results[resultKey] = result;
    }
  } finally {
    if (setInitialized) setInitialized(true);
  }

  return results;
};

export {
  postRequest,
  postFileRequest,
  getRequest,
  batchPostRequests,
  batchGetRequests
};